import React, { useState } from 'react';
import { IonPage, IonContent } from '@ionic/react';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useHomeLayoutStore } from '../../store/homeLayoutStore';
import { useHistory } from 'react-router-dom';
import Icon from '../../components/Icon';
import BackHeader from '../../components/BackHeader';
import { showToast } from '../../components/Toast';

const TOUR_STEPS = [
  { icon: 'format_list_bulleted', color: '#e9c46a', title: 'Shared Lists', desc: 'Shopping lists sync in real-time. Add items and your family sees them instantly.' },
  { icon: 'calendar_month', color: '#b39ddb', title: 'Family Calendar', desc: 'See everyone\'s events at a glance. Discuss plans right on each event.' },
  { icon: 'restaurant', color: '#ff8a65', title: 'Meal Planner', desc: 'Plan weekly meals and auto-add ingredients to your shopping list.' },
  { icon: 'check_circle', color: '#81c784', title: 'Tasks & Rewards', desc: 'Assign chores, earn points, and track who\'s pulling their weight.' },
];

// "What hurts?" from onboarding-desktop.pdf — pain-point first, feature second.
const FEATURE_OPTIONS = [
  { id: 'calendar', icon: 'calendar_month', label: '📅 Calendar chaos', desc: 'Everyone\'s events in one place.', color: '#b39ddb' },
  { id: 'budget', icon: 'account_balance_wallet', label: '🧾 Money feels blurry', desc: 'Bills, budgets, who spent what.', color: '#4fc3f7' },
  { id: 'school', icon: 'school', label: '🏫 School admin', desc: 'Forms, trips, letters, term dates.', color: '#b39ddb' },
  { id: 'meals', icon: 'restaurant', label: '🍲 Meals & shopping', desc: 'Plan the week, the list writes itself.', color: '#4fc3f7' },
  { id: 'lists', icon: 'format_list_bulleted', label: '🛒 Shared lists', desc: 'Real-time ticks at the shop.', color: '#e9c46a' },
  { id: 'pantry', icon: 'inventory_2', label: '🥫 Pantry', desc: 'What\'s in, what\'s running out.', color: '#a5d6a7' },
  { id: 'health', icon: 'local_hospital', label: '💊 Medical & meds', desc: 'Jabs, scripts, appointments.', color: '#ef5350' },
  { id: 'special-days', icon: 'star', label: '🎁 Birthdays & gifts', desc: 'Never miss one. Wishlists too.', color: '#ff8a65' },
  { id: 'tasks', icon: 'check_circle', label: '✅ Chores & tasks', desc: 'Fair-share, point-scored.', color: '#81c784' },
  { id: 'chat', icon: 'chat_bubble', label: '💬 Family chat', desc: 'One thread for the house.', color: '#80cbc4' },
  { id: 'recipes', icon: 'soup_kitchen', label: '📖 Recipes', desc: 'The ones that actually worked.', color: '#bcaaa4' },
  { id: 'docs', icon: 'folder_open', label: '📄 Docs & notes', desc: 'Passports, letters, bills.', color: '#64b5f6' },
  { id: 'contacts', icon: 'contacts', label: '📇 Contacts', desc: 'People, not alphabet soup.', color: '#f48fb1' },
  { id: 'today', icon: 'wb_sunny', label: '☀️ Today', desc: 'One screen, one day.', color: '#ffcc02' },
  { id: 'feed', icon: 'dynamic_feed', label: '📰 Family feed', desc: 'What happened while you were out.', color: '#80cbc4' },
  { id: 'map', icon: 'location_on', label: '📍 Family map', desc: 'Places, not surveillance.', color: '#80deea' },
  { id: 'inbox', icon: 'mail', label: '📬 School inbox', desc: 'Arbor, ParentMail, paper.', color: '#ff8a65' },
];

const DEFAULT_SELECTED = new Set(['calendar', 'tasks', 'lists', 'chat', 'meals', 'pantry']);

const FamilySetupPage: React.FC = () => {
  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [familyName, setFamilyName] = useState('');
  // onboarding-desktop.pdf step 02 "Your home" → address + postcode + colour
  const [householdAddress, setHouseholdAddress] = useState('');
  const [householdPostcode, setHouseholdPostcode] = useState('');
  const [householdColour, setHouseholdColour] = useState('#c5512d');
  const HOUSEHOLD_COLOURS = ['#c5512d', '#426464', '#48653f', '#a06bb8', '#e9c46a', '#64b5f6', '#f48fb1', '#8a7268'];
  const [inviteCode, setInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showFeaturePicker, setShowFeaturePicker] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(new Set(DEFAULT_SELECTED));
  const [tourStep, setTourStep] = useState(0);
  // Step 03 "Who lives here?" — members queued before sending invites.
  // Adults are queued by email and submitted to /family/invite-email when
  // the user finishes; kids are queued by display name and submitted to
  // /family/dependents (no login). See onboarding-mobile.pdf step 03.
  const [showMembersStep, setShowMembersStep] = useState(false);
  const [pendingMembers, setPendingMembers] = useState<Array<{ id: string; type: 'adult' | 'kid'; name: string; email?: string; age?: string }>>([]);
  const [addMemberType, setAddMemberType] = useState<'adult' | 'kid' | null>(null);
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberAge, setMemberAge] = useState('');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [createdFamilyName, setCreatedFamilyName] = useState('');
  const [createdInviteCode, setCreatedInviteCode] = useState('');
  const [showInviteStep, setShowInviteStep] = useState(false);
  const { setFamily, refreshFamily } = useAuthStore();
  const { bulkSetVisibility, markSetupDone } = useHomeLayoutStore();
  const history = useHistory();

  const toggleFeature = (id: string) => {
    setSelectedFeatures(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const shareInvite = async (code: string) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Join my family on FamHearth',
          text: `Use invite code ${code} to join our family on FamHearth!`,
          url: `https://famhearth.com/join/${code}`,
        });
      } else {
        await navigator.clipboard.writeText(code);
        showToast('Invite code copied!', 'success');
      }
    } catch {
      // User cancelled share dialog — ignore
    }
  };

  const handleFinishSetup = () => {
    bulkSetVisibility(selectedFeatures);
    markSetupDone();
    history.replace('/app/home');
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!familyName.trim()) { setError('Please enter a family name'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await api.post('/family/create', {
        name: familyName.trim(),
        address_line: householdAddress.trim() || null,
        postcode: householdPostcode.trim().toUpperCase() || null,
        colour: householdColour,
      });
      setFamily(res.data.family);
      await refreshFamily();
      setCreatedFamilyName(familyName.trim());
      setCreatedInviteCode(res.data.family.invite_code || res.data.inviteCode || '');
      // Step 02 done → step 03 "Who lives here?". The standalone invite-code
      // step still exists as a fallback users can reach via "Show invite code".
      setShowMembersStep(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create family');
    } finally { setIsLoading(false); }
  };

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) { setError('Please enter an invite code'); return; }
    setIsLoading(true); setError('');
    try {
      const res = await api.post('/family/join', { inviteCode: inviteCode.trim().toUpperCase() });
      setFamily(res.data.family);
      await refreshFamily();
      setCreatedFamilyName(res.data.family.name);
      setShowWelcome(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid invite code');
    } finally { setIsLoading(false); }
  };

  // If invite step active but no code available, skip to welcome
  if (showInviteStep && !createdInviteCode) {
    setShowInviteStep(false);
    setShowWelcome(true);
  }

  // Save a queued member to local list. Adults need an email; kids need a name.
  const queueMember = () => {
    const name = memberName.trim();
    if (!name) return;
    if (addMemberType === 'adult' && !memberEmail.trim()) return;
    setPendingMembers(prev => [...prev, {
      id: `m-${Date.now()}`,
      type: addMemberType!,
      name,
      ...(addMemberType === 'adult' ? { email: memberEmail.trim().toLowerCase() } : {}),
      ...(addMemberType === 'kid' && memberAge.trim() ? { age: memberAge.trim() } : {}),
    }]);
    setAddMemberType(null);
    setMemberName(''); setMemberEmail(''); setMemberAge('');
  };

  // Submit all queued members: dependents get rows in family_members; adults
  // each get an invite email. Routes to feature picker on success.
  const handleSendInvitesAndContinue = async () => {
    setSendingInvites(true);
    const errors: string[] = [];
    for (const m of pendingMembers) {
      try {
        if (m.type === 'kid') {
          await api.post('/family/dependents', { displayName: m.name, role: 'child' });
        } else if (m.email) {
          await api.post('/family/invite-email', { email: m.email });
        }
      } catch (err: any) {
        errors.push(`${m.name}: ${err.response?.data?.error || 'failed'}`);
      }
    }
    setSendingInvites(false);
    if (errors.length > 0) {
      showToast(`Some invites failed: ${errors[0]}`, 'error');
    } else if (pendingMembers.length > 0) {
      showToast(`${pendingMembers.length} added`, 'success');
    }
    await refreshFamily().catch(() => undefined);
    setShowMembersStep(false);
    setShowFeaturePicker(true);
  };

  // Step 03 "Who lives here?" — list current + queued members, add adults
  // (email invite) or kids (no login), then send + advance to step 04.
  if (showMembersStep) {
    const adminName = (useAuthStore.getState().user as any)?.displayName
      || (useAuthStore.getState().user as any)?.display_name
      || 'You';
    const initial = (s: string) => (s || '?').trim().charAt(0).toUpperCase();
    return (
      <IonPage>
        <IonContent>
          <div className="fh-auth-page">
            <div style={{ width: '100%', maxWidth: 420, padding: '24px 20px 120px', margin: '0 auto' }}>
              {/* Step indicator — 4 dots, third active */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 24 }}>
                {[0, 1, 2, 3].map(i => (
                  <div key={i} style={{
                    width: i === 2 ? 24 : 8, height: 6, borderRadius: 3,
                    background: i === 2 ? '#c5512d' : 'rgba(255,255,255,0.18)',
                  }} />
                ))}
              </div>
              <h1 style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: 24, fontWeight: 800, color: 'var(--fh-dark-text)', marginBottom: 6, letterSpacing: '-0.02em' }}>Who lives here?</h1>
              <p style={{ color: 'var(--fh-dark-text-secondary)', fontSize: 14, lineHeight: 1.55, marginBottom: 20 }}>
                Add adults, kids and extended family. You can always add more later.
              </p>

              {/* Member list — admin first, then queued */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: 'rgba(197,81,45,0.08)', border: '1px solid rgba(197,81,45,0.22)' }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#c5512d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                    {initial(adminName)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{adminName}</div>
                    <div style={{ fontSize: 11, color: 'var(--fh-dark-text-secondary)' }}>You · Admin</div>
                  </div>
                </div>
                {pendingMembers.map((m, idx) => (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: m.type === 'kid' ? '#e9c46a' : '#426464', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                      {initial(m.name)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                        {m.name}{m.type === 'kid' && m.age ? ` · ${m.age}` : ''}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--fh-dark-text-secondary)' }}>
                        {m.type === 'adult' ? `Adult · invite by ${m.email}` : 'Kid · no login needed'}
                      </div>
                    </div>
                    <button
                      onClick={() => setPendingMembers(prev => prev.filter((_, i) => i !== idx))}
                      aria-label={`Remove ${m.name}`}
                      style={{ background: 'none', border: 'none', color: 'var(--fh-dark-text-muted)', cursor: 'pointer', padding: 4 }}
                    >
                      <Icon name="close" size={18} color="var(--fh-dark-text-muted)" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Inline add form — hidden until "Add adult" / "Add kid" pressed */}
              {addMemberType !== null ? (
                <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fh-dark-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                    {addMemberType === 'adult' ? 'Add adult' : 'Add kid'}
                  </div>
                  <input
                    autoFocus
                    type="text"
                    value={memberName}
                    onChange={e => setMemberName(e.target.value)}
                    placeholder="Name"
                    style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, marginBottom: 8, outline: 'none' }}
                  />
                  {addMemberType === 'adult' ? (
                    <input
                      type="email"
                      value={memberEmail}
                      onChange={e => setMemberEmail(e.target.value)}
                      placeholder="Email for invite"
                      style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, marginBottom: 10, outline: 'none' }}
                    />
                  ) : (
                    <input
                      type="text"
                      value={memberAge}
                      onChange={e => setMemberAge(e.target.value)}
                      placeholder="Age (optional)"
                      style={{ width: '100%', padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, marginBottom: 10, outline: 'none' }}
                    />
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => { setAddMemberType(null); setMemberName(''); setMemberEmail(''); setMemberAge(''); }}
                      className="fh-btn-ghost"
                      style={{ flex: 1 }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={queueMember}
                      disabled={!memberName.trim() || (addMemberType === 'adult' && !memberEmail.trim())}
                      className="fh-btn-primary"
                      style={{ flex: 1, opacity: (!memberName.trim() || (addMemberType === 'adult' && !memberEmail.trim())) ? 0.4 : 1 }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                  <button
                    onClick={() => setAddMemberType('adult')}
                    className="fh-btn-ghost"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <Icon name="person_add" size={16} color="currentColor" />
                    Add adult
                  </button>
                  <button
                    onClick={() => setAddMemberType('kid')}
                    className="fh-btn-ghost"
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                  >
                    <Icon name="child_care" size={16} color="currentColor" />
                    Add kid
                  </button>
                </div>
              )}

              {/* Continue */}
              <button
                onClick={handleSendInvitesAndContinue}
                disabled={sendingInvites}
                className="fh-btn-primary"
                style={{ width: '100%', opacity: sendingInvites ? 0.6 : 1 }}
              >
                {sendingInvites
                  ? 'Sending…'
                  : pendingMembers.length === 0
                    ? 'Skip for now'
                    : `Send invites & continue${pendingMembers.length > 0 ? ` (${pendingMembers.length})` : ''}`}
              </button>
              <button
                onClick={() => { setShowMembersStep(false); setShowInviteStep(true); }}
                className="fh-btn-ghost"
                style={{ width: '100%', marginTop: 10 }}
              >
                Show invite code instead
              </button>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Share invite step — shown after family creation
  if (showInviteStep && createdInviteCode) {
    return (
      <IonPage>
        <IonContent fullscreen scrollY={false}>
          <div className="fh-auth-page">
            <div style={{ width: '100%', maxWidth: 380, padding: '0 24px', textAlign: 'center' }}>
              {/* Icon */}
              <div style={{
                width: 88, height: 88, borderRadius: 22, margin: '0 auto 28px',
                background: 'rgba(164,57,23,0.12)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name="group_add" size={40} color="#c5512d" />
              </div>

              <h2 style={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: 24, fontWeight: 800, color: 'var(--fh-dark-text)',
                marginBottom: 12, letterSpacing: '-0.02em',
              }}>Invite your family</h2>
              <p style={{
                color: 'var(--fh-dark-text-secondary)', fontSize: 15, lineHeight: 1.6,
                marginBottom: 32,
              }}>
                Share this code with your family members so they can join "{createdFamilyName}".
              </p>

              {/* Invite code display */}
              <div style={{
                background: 'var(--fh-dark-card)', borderRadius: 16, padding: '24px 20px',
                marginBottom: 24, border: '2px solid rgba(197,81,45,0.2)',
              }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--fh-dark-text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>
                  Your invite code
                </p>
                <p style={{
                  fontFamily: 'monospace', fontSize: 36, fontWeight: 800,
                  color: 'var(--fh-primary-light)', letterSpacing: '0.2em',
                }}>
                  {createdInviteCode}
                </p>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={() => shareInvite(createdInviteCode)}
                  className="fh-btn-primary"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  <Icon name="share" size={20} color="#fff" />
                  Share Invite Link
                </button>
                <button
                  onClick={() => { setShowInviteStep(false); setShowWelcome(true); }}
                  className="fh-btn-ghost"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Feature picker — choose which tiles to show on dashboard
  if (showFeaturePicker) {
    return (
      <IonPage>
        <IonContent>
          <div style={{ minHeight: '100vh', background: 'var(--fh-dark-bg)', fontFamily: 'Manrope, sans-serif' }}>
            <div style={{ padding: '60px 20px 120px', maxWidth: 440, margin: '0 auto' }}>
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 16, margin: '0 auto 16px',
                  background: 'rgba(164,57,23,0.12)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="dashboard_customize" size={32} color="#c5512d" />
                </div>
                <h1 style={{
                  fontFamily: '"Plus Jakarta Sans", sans-serif',
                  fontSize: 24, fontWeight: 800, color: 'var(--fh-dark-text)',
                  marginBottom: 8, letterSpacing: '-0.02em',
                }}>
                  Pick your features
                </h1>
                <p style={{ color: 'var(--fh-dark-text-secondary)', fontSize: 14, lineHeight: 1.6 }}>
                  Choose what matters to your family. You can always change this later in Settings.
                </p>
              </div>

              {/* Counter */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 16, padding: '0 4px',
              }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fh-dark-text-muted)' }}>
                  {selectedFeatures.size} selected
                </span>
                <button
                  onClick={() => setSelectedFeatures(new Set(DEFAULT_SELECTED))}
                  style={{ background: 'none', border: 'none', color: 'var(--fh-primary-light)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  Reset to essentials
                </button>
              </div>

              {/* Feature grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                {FEATURE_OPTIONS.map(feat => {
                  const selected = selectedFeatures.has(feat.id);
                  return (
                    <button
                      key={feat.id}
                      onClick={() => toggleFeature(feat.id)}
                      style={{
                        background: selected ? `${feat.color}12` : 'rgba(255,255,255,0.03)',
                        border: selected ? `2px solid ${feat.color}50` : '2px solid rgba(255,255,255,0.06)',
                        borderRadius: 12, padding: '14px 12px',
                        display: 'flex', alignItems: 'center', gap: 10,
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: selected ? `${feat.color}20` : 'var(--fh-overlay-subtle)',
                      }}>
                        <Icon name={feat.icon} size={20} color={selected ? feat.color : 'var(--fh-dark-text-muted)'} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: selected ? '#fff' : 'var(--fh-dark-text-secondary)' }}>
                          {feat.label}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--fh-dark-text-muted)', marginTop: 1 }}>
                          {feat.desc}
                        </div>
                      </div>
                      <div style={{
                        width: 22, height: 22, borderRadius: 6, flexShrink: 0,
                        background: selected ? feat.color : 'var(--fh-overlay-light)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'all 0.15s',
                      }}>
                        {selected && <Icon name="check" size={14} color="#fff" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Continue button */}
              <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 20px calc(16px + env(safe-area-inset-bottom, 0px))', background: 'linear-gradient(transparent, var(--fh-dark-bg) 30%)', zIndex: 10 }}>
                <button
                  onClick={handleFinishSetup}
                  disabled={selectedFeatures.size === 0}
                  className="fh-btn-primary"
                  style={{ width: '100%', opacity: selectedFeatures.size === 0 ? 0.4 : 1 }}
                >
                  Continue with {selectedFeatures.size} feature{selectedFeatures.size !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Feature tour after welcome
  if (showTour) {
    const step = TOUR_STEPS[tourStep];
    return (
      <IonPage>
        <IonContent fullscreen scrollY={false}>
          <div className="fh-auth-page">
            <div style={{ width: '100%', maxWidth: 380, padding: '0 24px', textAlign: 'center' }}>
              {/* Step indicator */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 48 }}>
                {TOUR_STEPS.map((_, i) => (
                  <div key={i} style={{
                    width: i === tourStep ? 24 : 8, height: 8, borderRadius: 4,
                    background: i === tourStep ? '#c5512d' : 'rgba(255,255,255,0.15)',
                    transition: 'all 0.3s',
                  }} />
                ))}
              </div>

              {/* Icon */}
              <div style={{
                width: 88, height: 88, borderRadius: 22, margin: '0 auto 28px',
                background: `${step.color}15`, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon name={step.icon} size={40} color={step.color} />
              </div>

              {/* Text */}
              <h2 style={{
                fontFamily: '"Plus Jakarta Sans", sans-serif',
                fontSize: 26, fontWeight: 800, color: 'var(--fh-dark-text)',
                marginBottom: 12, letterSpacing: '-0.02em',
              }}>{step.title}</h2>
              <p style={{
                color: 'var(--fh-dark-text-secondary)', fontSize: 15, lineHeight: 1.6,
                marginBottom: 48,
              }}>{step.desc}</p>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={() => {
                    if (tourStep < TOUR_STEPS.length - 1) {
                      setTourStep(tourStep + 1);
                    } else {
                      setShowTour(false);
                      setShowFeaturePicker(true);
                    }
                  }}
                  className="fh-btn-primary"
                >
                  {tourStep < TOUR_STEPS.length - 1 ? 'Next' : 'Choose Your Features'}
                </button>
                <button
                  onClick={() => { setShowTour(false); setShowFeaturePicker(true); }}
                  className="fh-btn-ghost"
                >
                  Skip to setup
                </button>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  // Welcome celebration modal
  if (showWelcome) {
    return (
      <IonPage>
        <IonContent fullscreen scrollY={false}>
          <div className="fh-auth-page">
            <div className="fh-welcome-modal">
              <div className="fh-welcome-icon-wrap">
                <Icon name="celebration" size={24} />
              </div>

              <h1 className="fh-welcome-title">
                Welcome to<br />your FamHearth
              </h1>

              <p className="fh-welcome-desc">
                {mode === 'create'
                  ? `Your family "${createdFamilyName}" has been created! Invite your loved ones to share lists, events, meals, and more.`
                  : `You've joined "${createdFamilyName}"! Start sharing lists, events, meals, and more with your family.`
                }
              </p>

              {/* Invite code display in welcome modal */}
              {createdInviteCode && (
                <div style={{
                  background: 'var(--fh-overlay-subtle)', borderRadius: 12, padding: '16px',
                  marginBottom: 8, border: '1px solid rgba(197,81,45,0.2)',
                }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fh-dark-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Invite code
                  </p>
                  <p style={{
                    fontFamily: 'monospace', fontSize: 28, fontWeight: 800,
                    color: 'var(--fh-primary-light)', letterSpacing: '0.15em', marginBottom: 12,
                  }}>
                    {createdInviteCode}
                  </p>
                  <button
                    onClick={() => shareInvite(createdInviteCode)}
                    style={{
                      background: 'rgba(197,81,45,0.15)', color: 'var(--fh-primary-light)',
                      border: 'none', borderRadius: 8, padding: '8px 16px',
                      fontSize: 13, fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto',
                    }}
                  >
                    <Icon name="share" size={16} color="#c5512d" />
                    Share Invite Link
                  </button>
                </div>
              )}

              <div className="fh-welcome-actions">
                <button
                  onClick={() => { setShowWelcome(false); setShowFeaturePicker(true); }}
                  className="fh-btn-primary"
                >
                  Choose Your Features
                </button>
                <button
                  onClick={() => { setShowWelcome(false); setShowTour(true); }}
                  className="fh-btn-ghost"
                >
                  Take a tour first
                </button>
              </div>
            </div>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <BackHeader title="Family Setup" />
        <div className="fh-auth-page">
          <div className="fh-auth-content">
            {/* Header icon */}
            <div className="fh-setup-hero">
              <div className="fh-hero-icon-wrap">
                <Icon name={mode === 'create' ? 'home' : 'link'} size={24} />
              </div>
            </div>

            <div className="fh-onboarding-text">
              <h1 className="fh-onboarding-title">Set up your family</h1>
              <p className="fh-onboarding-desc">
                Create a new family group or join one with an invite code.
              </p>
            </div>

            {/* Mode toggle */}
            <div className="fh-toggle-wrap">
              <button
                onClick={() => { setMode('create'); setError(''); }}
                className={`fh-toggle-btn ${mode === 'create' ? 'fh-toggle-active' : ''}`}
              >
                Create family
              </button>
              <button
                onClick={() => { setMode('join'); setError(''); }}
                className={`fh-toggle-btn ${mode === 'join' ? 'fh-toggle-active' : ''}`}
              >
                Join family
              </button>
            </div>

            {/* Form */}
            {mode === 'create' ? (
              <form onSubmit={handleCreate} className="fh-auth-form">
                <div className="fh-input-group">
                  <label className="fh-input-label">Household name</label>
                  <input
                    type="text"
                    value={familyName}
                    onChange={(e) => setFamilyName(e.target.value)}
                    placeholder="e.g. The Adejoros"
                    autoFocus
                    className="fh-input"
                  />
                </div>
                <div className="fh-input-group">
                  <label className="fh-input-label">Where (optional)</label>
                  <input
                    type="text"
                    value={householdAddress}
                    onChange={(e) => setHouseholdAddress(e.target.value)}
                    placeholder="e.g. 42 Beacon Road"
                    className="fh-input"
                  />
                </div>
                <div className="fh-input-group">
                  <label className="fh-input-label">Postcode (optional)</label>
                  <input
                    type="text"
                    value={householdPostcode}
                    onChange={(e) => setHouseholdPostcode(e.target.value.toUpperCase())}
                    placeholder="e.g. N16 7PL"
                    className="fh-input"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>
                <div className="fh-input-group">
                  <label className="fh-input-label">Household colour</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', padding: '6px 0' }}>
                    {HOUSEHOLD_COLOURS.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setHouseholdColour(c)}
                        aria-label={`Pick ${c}`}
                        style={{
                          width: 36, height: 36, borderRadius: '50%',
                          background: c, cursor: 'pointer',
                          border: householdColour === c ? '3px solid #fff' : '2px solid transparent',
                          boxShadow: householdColour === c ? `0 0 0 2px ${c}` : 'none',
                          padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>
                {error && <p className="fh-error">{error}</p>}
                <button
                  type="submit"
                  disabled={isLoading || !familyName.trim()}
                  className="fh-btn-primary"
                >
                  {isLoading ? 'Creating...' : 'Create household'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleJoin} className="fh-auth-form">
                <div className="fh-input-group">
                  <label className="fh-input-label">Invite code</label>
                  <input
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="e.g. ABC123"
                    maxLength={6}
                    autoFocus
                    className="fh-input fh-input-code"
                  />
                </div>
                {error && <p className="fh-error">{error}</p>}
                <button
                  type="submit"
                  disabled={isLoading || inviteCode.length < 4}
                  className="fh-btn-primary"
                >
                  {isLoading ? 'Joining...' : 'Join Family'}
                </button>
              </form>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default FamilySetupPage;
